import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { 
  FaGavel, 
  FaUserShield, 
  FaHandshake, 
  FaExclamationTriangle,
  FaCreditCard,
  FaShieldAlt
} from 'react-icons/fa';

const Terms = () => {
  const lastUpdated = "September 25, 2025";

  const sections = [
    {
      id: "acceptance",
      title: "Acceptance of Terms",
      icon: <FaHandshake className="h-6 w-6" />,
      content: [
        {
          text: "By accessing and using Boulevard's services, including our Telegram bot and website, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service."
        },
        {
          text: "These Terms of Service constitute a legally binding agreement between you and Boulevard. By using our community services, you represent that you are at least 18 years old and have the legal capacity to enter into this agreement."
        }
      ]
    },
    {
      id: "service-description",
      title: "Service Description",
      icon: <FaUserShield className="h-6 w-6" />,
      content: [
        {
          subtitle: "Platform Services",
          text: "Boulevard provides a community ride-sharing platform that connects drivers and riders through our Telegram bot service. As a community service by iautenticai, we facilitate introductions and provide tools for coordination, but we are not a transportation provider."
        },
        {
          subtitle: "User Roles",
          text: "Users can register as drivers (offering rides) or riders (seeking rides). Drivers must possess valid licenses and insurance. All users must complete identity verification before using the service."
        },
        {
          subtitle: "Technology Platform",
          text: "Our service operates primarily through Telegram bots, with additional web-based tools for account management and analytics. We use AI-powered matching algorithms to connect compatible users."
        },
        {
          subtitle: "Community Service Mission",
          text: "Boulevard is operated as a community service by iautenticai company. We charge zero platform fees and operate with the mission to serve the community, not generate profit. Our goal is to provide fair, transparent ride-sharing connections."
        }
      ]
    },
    {
      id: "user-responsibilities",
      title: "User Responsibilities",
      icon: <FaGavel className="h-6 w-6" />,
      content: [
        {
          subtitle: "Account Security",
          text: "You are responsible for maintaining the confidentiality of your account information and for all activities that occur under your account. You must notify us immediately of any unauthorized use."
        },
        {
          subtitle: "Accurate Information",
          text: "You agree to provide accurate, current, and complete information during registration and to update such information to maintain its accuracy."
        },
        {
          subtitle: "Compliance with Laws",
          text: "You agree to comply with all applicable laws and regulations when using our service, including traffic laws, insurance requirements, and local transportation regulations."
        },
        {
          subtitle: "Respectful Conduct",
          text: "You agree to treat other users with respect and courtesy. Harassment, discrimination, or inappropriate behavior may result in account suspension or termination."
        }
      ]
    },
    {
      id: "driver-specific",
      title: "Driver-Specific Terms",
      icon: <FaCreditCard className="h-6 w-6" />,
      content: [
        {
          subtitle: "Vehicle Requirements",
          text: "Drivers must own or have legal access to a safe, reliable vehicle that meets local safety standards and regulations. The vehicle must be properly registered and insured."
        },
        {
          subtitle: "License and Insurance",
          text: "Drivers must possess a valid driver's license and maintain appropriate insurance coverage, including commercial or rideshare insurance as required by local laws."
        },
        {
          subtitle: "Passenger Safety",
          text: "Drivers are responsible for ensuring passenger safety during rides, including following traffic laws, maintaining their vehicle in safe condition, and providing a secure transportation experience."
        },
        {
          subtitle: "Pricing and Payments",
          text: "Drivers may set their own pricing within reasonable limits or use our suggested rates. All payments are processed through our secure payment system."
        }
      ]
    },
    {
      id: "prohibited-uses",
      title: "Prohibited Uses",
      icon: <FaExclamationTriangle className="h-6 w-6" />,
      content: [
        {
          subtitle: "Illegal Activities",
          text: "You may not use our service for any illegal or unauthorized purpose, including but not limited to transporting illegal goods or engaging in criminal activities."
        },
        {
          subtitle: "System Abuse",
          text: "You may not attempt to interfere with, compromise, or disrupt our service, servers, or networks. This includes attempting to gain unauthorized access to user accounts or system data."
        },
        {
          subtitle: "False Information",
          text: "You may not provide false, misleading, or fraudulent information during registration or while using our service. Identity verification fraud will result in immediate account termination."
        },
        {
          subtitle: "Commercial Restrictions",
          text: "Individual users may not use our platform for commercial transportation services beyond casual ridesharing. Professional transportation services require separate commercial agreements."
        }
      ]
    },
    {
      id: "liability",
      title: "Limitation of Liability",
      icon: <FaShieldAlt className="h-6 w-6" />,
      content: [
        {
          subtitle: "Service Disclaimer",
          text: "Boulevard is a community service technology platform that connects users. We do not provide transportation services directly and are not responsible for the actions of drivers or riders."
        },
        {
          subtitle: "User Interactions",
          text: "You acknowledge that all interactions with other users are at your own risk. We recommend exercising caution and following safety guidelines when meeting or traveling with other users."
        },
        {
          subtitle: "Damages Limitation",
          text: "To the maximum extent permitted by law, RideEase shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our service."
        },
        {
          subtitle: "Maximum Liability",
          text: "Our total liability to you for any claims arising from these terms or your use of our community service shall not exceed the amount you have paid to us in the twelve months preceding the claim."
        }
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>Terms of Service - Boulevard</title>
        <meta name="description" content="Boulevard Terms of Service - Read the terms and conditions for using our community ride-sharing platform and Telegram bot services." />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-20 pb-16 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Terms of Service
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              Please read these terms carefully before using Boulevard services. 
              By using our community platform, you agree to these terms and conditions.
            </p>
            <p className="text-sm text-gray-500">
              Last updated: {lastUpdated}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-amber-50 border-l-4 border-amber-400 p-6"
          >
            <div className="flex items-start">
              <FaExclamationTriangle className="text-amber-400 mt-1 mr-3 flex-shrink-0" />
              <div>
                <h2 className="text-xl font-semibold text-amber-800 mb-3">
                  Important Legal Agreement
                </h2>
                <p className="text-amber-700">
                  These Terms of Service contain important information about your rights and 
                  obligations when using Boulevard. Please read them carefully. By using our 
                  community service, you agree to be legally bound by these terms.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {sections.map((section, sectionIndex) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: sectionIndex * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-8"
              >
                <div className="flex items-center space-x-3 mb-6">
                  <div className="text-primary-600">
                    {section.icon}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {section.title}
                  </h2>
                </div>

                <div className="space-y-6">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      {item.subtitle && (
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {item.subtitle}
                        </h3>
                      )}
                      <p className="text-gray-600 leading-relaxed">
                        {item.text}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Terms */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <FaCreditCard className="text-primary-600 mr-3" />
              Payment Terms
            </h2>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Service Fees
                </h3>
                <p className="text-gray-600">
                  RideEase charges a service fee for each completed ride to cover platform 
                  maintenance, support, and development costs. Fees are transparently 
                  displayed before transaction completion.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Payment Processing
                </h3>
                <p className="text-gray-600">
                  All payments are processed securely through our payment partners. We support 
                  major credit cards, debit cards, and digital wallets. Payment information 
                  is encrypted and stored securely.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Refunds and Disputes
                </h3>
                <p className="text-gray-600">
                  Refunds may be issued in cases of service failure or legitimate disputes. 
                  All refund requests are reviewed on a case-by-case basis. Users can contact 
                  support to initiate the dispute resolution process.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Account Termination */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Account Termination
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Termination by User
                </h3>
                <p className="text-gray-600">
                  You may terminate your account at any time by contacting our support team 
                  or using the account deletion feature in our platform. Upon termination, 
                  your personal data will be deleted according to our privacy policy.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Termination by RideEase
                </h3>
                <p className="text-gray-600">
                  We reserve the right to suspend or terminate accounts that violate these 
                  terms, engage in fraudulent activity, or pose a safety risk to other users. 
                  We will provide notice when possible.
                </p>
              </div>
            </div>

                        <div className="mt-6 p-4 bg-accent-50 border border-accent-300 rounded-lg">
              <h4 className="font-semibold text-accent-800 mb-2">
                Important Notices:
              </h4>
              <ul className="text-accent-700 text-sm space-y-1">
                <li>• Services may be suspended during maintenance</li>
                <li>• Account termination may result in data deletion</li>
                <li>• Emergency contact information should be kept current</li>
                <li>• Report violations immediately through our support channels</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Governing Law */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white rounded-xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Governing Law and Dispute Resolution
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Applicable Law
                </h3>
                <p className="text-gray-600">
                  These Terms of Service are governed by and construed in accordance with 
                  the laws of the jurisdiction where RideEase is incorporated, without 
                  regard to conflict of law principles.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Dispute Resolution
                </h3>
                <p className="text-gray-600">
                  We encourage users to contact us first to resolve any disputes. If direct 
                  resolution is not possible, disputes may be resolved through binding 
                  arbitration in accordance with applicable arbitration rules.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Class Action Waiver
                </h3>
                <p className="text-gray-600">
                  To the extent permitted by law, you agree that any dispute resolution 
                  proceedings will be conducted only on an individual basis and not in a 
                  class, consolidated, or representative action.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact and Changes */}
      <section className="py-12 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-3xl font-bold mb-6">
              Questions About These Terms?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              If you have any questions about these Terms of Service, please contact 
              our legal team for clarification.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto mb-8">
              <div>
                <h3 className="font-semibold mb-2">Legal Team</h3>
                <p className="text-primary-100">legal@rideease.app</p>
              </div>
              <div>
                <h3 className="font-semibold mb-2">General Support</h3>
                <p className="text-primary-100">support@rideease.app</p>
              </div>
            </div>

            <a
              href="/contact"
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg transition-colors inline-flex items-center space-x-2"
            >
              <span>Contact Us</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Changes Notice */}
      <section className="py-12 bg-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Changes to Terms
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We reserve the right to modify these Terms of Service at any time. 
              We will notify users of significant changes via email or platform 
              notifications. Continued use of our service after changes constitutes 
              acceptance of the new terms.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Terms;
